import dayjs from 'dayjs'
import kebabCase from 'lodash/kebabCase'

import { AppointmentScheduleTypes, AvailabilityType, getFriendlyDuration, TimeSlotDuration } from '@shared/utils'

export const format = 'YYYY-MM-DD HH:mm'
export const slotDuration = `00:${TimeSlotDuration}:00`
export const CalendarEvent = {
  Availability: 'availability',
  AdminTime: 'adminTime',
  OutOfOffice: 'ooo',
  Appointment: 'appointment',
  AdHocAppointment: 'adHocAppointment',
  OnDemandAppointment: 'onDemandAppointment',
  Blackout: 'blackout',
}
export const availabilityTypeToEvent = {
  [AvailabilityType.Appointment]: CalendarEvent.Availability,
  [AvailabilityType.Admin]: CalendarEvent.AdminTime,
  [AvailabilityType.OutOfOffice]: CalendarEvent.OutOfOffice,
}

export const calendarEventToString = {
  [CalendarEvent.Availability]: 'Appt. Availability',
  [CalendarEvent.AdminTime]: 'Admin Time',
  [CalendarEvent.OutOfOffice]: 'Out of Office',
  [CalendarEvent.AdHocAppointment]: 'Ad Hoc Appointment',
}

export const stringifyDuration = (duration) => {
  const hours = Math.floor(duration / 60)
  const minutes = duration % 60

  if (hours && minutes) return `${hours}h ${minutes}m`
  if (hours) return `${hours}h`
  return `${minutes}m`
}

export const getId = ({ id, type }) => `${type}-${id}`

const createAvailabilityEvent = (item, timezone) => {
  const approved = Boolean(item.approved)
  const start = dayjs(item.start).tz(timezone)
  const end = dayjs(item.end).tz(timezone)
  const duration = end.diff(start, 'minutes')
  const extendedProps = { id: item.id, type: CalendarEvent.Availability, approved }

  return {
    id: getId(extendedProps),
    start: start.format(format),
    end: end.format(format),
    duration,
    display: 'background',
    className: `availability-slot${approved ? '' : ' availability-pending'}`,
    extendedProps,
  }
}

const createAdminTimeEvent = (item, timezone) => {
  const approved = Boolean(item.approved)
  const start = dayjs(item.start).tz(timezone)
  const end = dayjs(item.end).tz(timezone)
  const duration = end.diff(start, 'minutes')
  const extendedProps = { id: item.id, type: CalendarEvent.AdminTime, approved }

  return {
    id: getId(extendedProps),
    start: start.format(format),
    end: end.format(format),
    duration,
    display: 'background',
    className: `admin-time-slot${approved ? '' : ' admin-time-slot-pending'}`,
    extendedProps,
  }
}

const createOutOfOfficeTimeEvent = (item, timezone) => {
  const approved = Boolean(item.approved)
  const start = dayjs(item.start).tz(timezone)
  const end = dayjs(item.end).tz(timezone)
  const duration = end.diff(start, 'minutes')
  const extendedProps = { id: item.id, type: CalendarEvent.OutOfOffice, approved }

  return {
    id: getId(extendedProps),
    start: start.format(format),
    end: end.format(format),
    duration,
    display: 'background',
    className: `out-of-office-time-slot${approved ? '' : ' out-of-office-time-slot-pending'}`,
    extendedProps,
  }
}

const createAppointmentEvent = (item, timezone) => {
  const type = item.schedType === AppointmentScheduleTypes.OnDemand ? CalendarEvent.OnDemandAppointment : CalendarEvent.Appointment
  const start = dayjs(item.start).tz(timezone)
  const endByDuration = start.add(item.duration || TimeSlotDuration, 'minutes')
  const end = item.end ? dayjs.min(dayjs(item.end).tz(timezone), endByDuration) : endByDuration
  const time = start.format('LT')
  const name = item.user.fullName

  const extendedProps = {
    id: item.id,
    type,
    tooltip: `${type === CalendarEvent.OnDemandAppointment ? 'On Demand - ' : ''}${time}  ${getFriendlyDuration(start, end)} - ${name} - ${item.status}`,
  }

  return {
    id: getId(extendedProps),
    start: start.format(format),
    end: end.format(format),
    title: `${type === CalendarEvent.OnDemandAppointment ? '❗️' : ''}${time} ${name}`,
    className: ['appointment-slot', kebabCase(item.status)],
    extendedProps,
  }
}

const createAdHocAppointmentEvent = (item, timezone) => {
  const start = dayjs(item.start).tz(timezone)
  const end = start.add(item.duration || TimeSlotDuration, 'minutes')
  const time = start.format('LT')
  const name = 'Ad Hoc'
  const extendedProps = {
    id: item.id,
    type: CalendarEvent.AdHocAppointment,
    tooltip: `${time}  ${getFriendlyDuration(start, end)} - ${name}`,
  }

  return {
    id: getId(extendedProps),
    start: start.format(format),
    end: end.format(format),
    title: `${time} ${name}`,
    className: 'ad-hoc-appointment-slot',
    extendedProps,
  }
}

const createBlackoutEvent = (item, timezone, additionalProps) => {
  let startBlackout, endBlackout, titleBlackout
  let extendedProps = { id: item.id, type: CalendarEvent.Blackout }

  if (item.allDay) {
    const dateBlackout = dayjs(item.allDayDate)
    startBlackout = `${dateBlackout.format('YYYY-MM-DD')} ${additionalProps.businessHours.start_time}`
    endBlackout = `${dateBlackout.format('YYYY-MM-DD')} ${additionalProps.businessHours.end_time}`
    titleBlackout = item.title
    extendedProps = {
      ...extendedProps,
      tooltip: `${dateBlackout.format('L')} - ${item.title}`,
    }
  } else {
    const startBlackoutDT = dayjs(item.start).tz(timezone)
    const endBlackoutDT = dayjs(item.end).tz(timezone)
    startBlackout = startBlackoutDT.format(format)
    endBlackout = endBlackoutDT.format(format)
    titleBlackout = `${startBlackoutDT.format('LT')} ${item.title}`
    extendedProps = {
      ...extendedProps,
      tooltip: `${startBlackoutDT.format('LT')} - ${endBlackoutDT.format('LT')} ${item.title}`,
    }
  }

  return {
    className: 'blackout-slot',
    title: titleBlackout,
    start: startBlackout,
    end: endBlackout,
    extendedProps,
  }
}

const typeToFunctionMap = {
  [CalendarEvent.Availability]: createAvailabilityEvent,
  [CalendarEvent.AdminTime]: createAdminTimeEvent,
  [CalendarEvent.OutOfOffice]: createOutOfOfficeTimeEvent,
  [CalendarEvent.Appointment]: createAppointmentEvent,
  [CalendarEvent.AdHocAppointment]: createAdHocAppointmentEvent,
  [CalendarEvent.Blackout]: createBlackoutEvent,
}

export const eventsFactory = (data = [], type, timezone, additionalProps = {}) => {
  const createEventFunction = typeToFunctionMap[type]
  if (!createEventFunction) return []
  return data.map((item) => createEventFunction(item, timezone, additionalProps))
}

// TODO optimize using right data structure
export const findAvailability = ({ start, end }, availabilities = []) => {
  const selectStart = dayjs(start)
  const selectEnd = dayjs(end)

  return availabilities.find((avail) => {
    if (!avail.extendedProps.approved) return false

    const availStart = dayjs(avail.start)
    const availEnd = dayjs(avail.end)

    return selectStart.isSameOrAfter(availStart) && selectEnd.isSameOrBefore(availEnd)
  })
}

// The calendar sizes itself to the page instead of the page growing to fit the calendar.
// Every wrapper between the page and the grid has to pass the available height down.
export const fillHeight = { flex: 1, minHeight: 0 }
